import React, {useContext, useState} from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {useQueryClient} from '@tanstack/react-query';
import {Table} from 'reactstrap';
import {IOrderTaskModel, OrderTaskPossibleTypes} from '@src/@interfaces/models/Order/IOrderTaskModel';
import VoidTask, {TaskRepresentationProps} from './representations/VoidTask';
import ChangeStatusTask from './representations/ChangeStatusTask';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import RenderIf from '@src/components/RenderIf/RenderIf';
import classnames from '@src/helpers/classnames';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import Translate from '@src/components/Translate/Translate';
import MomentDate from '@src/components/MomentDate/MomentDate';
import {FormFieldOption} from '@src/@types/components/Form';
import TaskTypeField from './TaskTypeField';
import app from '@src/helpers/app';
import ValidationHttpException from '@src/exceptions/ValidationHttpException';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';

const views: Record<OrderTaskPossibleTypes, React.ComponentType<TaskRepresentationProps<any>>> = {
    'change-status': ChangeStatusTask
};

export const taskTypesOptions: FormFieldOption[] = [
    'change-status',
].map(type => ({
    value: type,
    label: `TaskForm.types.${type}`,
}));

function isValidType(type?: string): type is OrderTaskPossibleTypes {
    return type != null && (type in views);
}

const styleWhiteSpaceNoWrap = {whiteSpace: 'nowrap'} as React.CSSProperties;

export default function TaskForm(props: {
    order: IOrderModel,
    task: IOrderTaskModel<any>,
    onDelete: () => Promise<boolean>,
    deleteWithConfirm?: boolean,
}) {
    const {order, task} = props;

    const [designContext] = useContext(FormFieldDesignContext);

    const [isDeleting, setIsDeleting] = useState(false);
    const [formId] = useState(String(Date.now() + Math.random()));
    const formApi = useForm<typeof props.task>({
        mode: 'onChange',
        defaultValues: task,
        criteriaMode: 'all',
    });
    const {control} = formApi;
    const [isOpened, setIsOpened] = useState(!task.id);
    const currentType = useWatch({control, name: 'type'});
    const payload = useWatch({control, name: 'payload'});

    const isCompleted = task.completed_at != null;
    const maxAttemptsExceeded = (
            !isCompleted &&
            task.attempts != null &&
            task.max_attempts != null &&
            task.attempts > task.max_attempts
    );
    const isDisabled = (
            designContext.fieldsAreReadOnly ||
            designContext.fieldsAreDisabled ||
            isCompleted ||
            maxAttemptsExceeded
    );

    const TaskFormView = isValidType(currentType) ? views[currentType] : VoidTask;
    const queryClient = useQueryClient();
    const onSubmitHandler: React.FormEventHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();

        return formApi.handleSubmit(data => {
            if (isDisabled) {
                return Promise.reject(new Error('Logic exception'));
            }

            const routeName = task.id ? 'orders.tasks.update' : 'orders.tasks.store';

            return app('http').route(routeName, {
                id: String(order.id),
                taskId: String(task.id),
            }, {
                headers: {'content-type': 'multipart/form-data'},
                body: {
                    'controller-name': 'Orders',
                    ...data
                },
            }).then(value => {
                if (!props.task.id && props.onDelete) {
                    props.onDelete().then();
                }

                const queryKey = app('queryKeys').getCustomQueryOfExactModelQueryKey(
                        'Orders', props.order?.id, 'tasks'
                );
                queryClient.refetchQueries({queryKey, exact: true}).then();

                return value;
            }).catch(e => {
                if (e instanceof ValidationHttpException) {
                    const error = e.getValidationErrors();
                    Object.keys(error).forEach(fieldName => {
                        formApi.setError(fieldName, {
                            message: error[fieldName].at(0)
                        });
                    });
                }

                return Promise.reject(e);
            });
        })(e);
    };

    const expanded = isOpened || !currentType;
    const columnNumber = 5 + (currentType === 'change-status' ? 1 : 0);

    return <FormProvider {...formApi}>
        <form onSubmit={onSubmitHandler} id={formId}>
            <Table className="rounded" bordered responsive>
                <tbody>
                    <tr>
                        <td valign="middle">{task.id ?? '—'}</td>
                        <td valign="middle">
                            <button
                                    type="button"
                                    className={classnames('btn btn-sm', {
                                        'btn-outline-secondary': expanded,
                                        'btn-outline-info': !expanded,
                                    })}
                                    disabled={!currentType}
                                    onClick={() => setIsOpened(state => !state)}
                            >
                                <i className="fa fa-pencil"/>
                            </button>
                        </td>
                        <td valign="middle" className="w-100">
                            {(() => {
                                const option = currentType ? taskTypesOptions.find(o => o.value === currentType) : null;
                                if (!currentType || !option) {
                                    return <Translate message="TaskForm.messages.select-type"
                                                      style={styleWhiteSpaceNoWrap}/>;
                                }

                                return <Translate message={option.label} style={styleWhiteSpaceNoWrap}/>;
                            })()}

                            {maxAttemptsExceeded ? (
                                    <Translate
                                            message="TaskForm.messages.max-attempts-exceeded"
                                            tag="div"
                                            className="alert alert-danger px-2 py-1 m-0 mt-2"
                                    />
                            ) : null}
                        </td>
                        {currentType === 'change-status' ? <td valign="middle" style={styleWhiteSpaceNoWrap}>
                            <div>
                                <Translate
                                        message="TaskForm.change-status.fields.will_change_at"
                                        style={styleWhiteSpaceNoWrap}
                                />:
                            </div>
                            {payload?.will_change_at ? (
                                    <MomentDate date={payload.will_change_at} format="L LT"/>
                            ) : '—'}
                        </td> : null}
                        <td valign="middle" style={styleWhiteSpaceNoWrap}>
                            <div>
                                <Translate message="TaskForm.fields.completed_at" style={styleWhiteSpaceNoWrap}/>:
                            </div>
                            {task.completed_at ? <MomentDate date={task.completed_at} format="L LT"/> : '—'}
                        </td>
                        <td valign="middle">
                            {props.deleteWithConfirm ? (
                                    <ConfirmableButton
                                            type="button"
                                            color="danger"
                                            size="sm"
                                            disabled={isDeleting}
                                            onClick={() => {
                                                if (!props.onDelete) {
                                                    return;
                                                }

                                                setIsDeleting(true);
                                                props.onDelete().finally(() => {
                                                    setIsDeleting(false);
                                                });
                                            }}
                                    >
                                        <i className="fa fa-times"/>
                                    </ConfirmableButton>
                            ) : (
                                    <button
                                            type="button"
                                            className="btn btn-sm btn-warning"
                                            onClick={props.onDelete}
                                    >
                                        <i className="fa fa-times"/>
                                    </button>
                            )}
                        </td>
                    </tr>

                    <RenderIf expression={expanded}>
                        <tr>
                            <td colSpan={columnNumber}>
                                {
                                    currentType ?
                                            <TaskFormView
                                                    formId={formId}
                                                    task={task}
                                                    order={order}
                                                    isDisabled={isDisabled}
                                            /> :
                                            <TaskTypeField formId={formId} task={task}/>
                                }

                                {isDisabled ? null : <div className="mt-3">
                                    <Translate<HTMLButtonElement>
                                            message="Forms.save_changes"
                                            tag="button"
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={onSubmitHandler}
                                            form={formId}
                                    />
                                </div>}
                            </td>
                        </tr>
                    </RenderIf>
                </tbody>
            </Table>
        </form>
    </FormProvider>;
}
